import { createFileRoute } from "@tanstack/react-router";
import { MerchantExperienceBar } from "@/components/renata/MerchantExperienceBar";
import { useCallback, useEffect, useState } from "react";
import { ClinicalPageShell } from "@/components/clinical";
import { CrudShellHeader, Loading } from "@/components/ds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CalendarSearch, Copy, Loader2, Trash2 } from "lucide-react";
import { apiDelete, apiGetJson, apiSendJson } from "@/lib/sm-api";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { toast } from "sonner";
import type { ServiceProfessional, ServiceSchedule } from "@/lib/db-service/service-professionals";
import type { AgendaBlock } from "@/lib/db-service/service-agenda-blocks";
import type { ServiceCatalogItem } from "@/lib/db-service/service-catalog";

export const Route = createFileRoute("/app/horarios")({
  validateSearch: (s: Record<string, unknown>) => ({
    professionalId: typeof s.professionalId === "string" ? s.professionalId : undefined,
  }),
  component: Page,
  head: () => ({ meta: [{ title: "Horarios" }] }),
});

const DAYS = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];

type DayRow = { active: boolean; startTime: string; endTime: string };

function emptyWeek(): DayRow[] {
  return DAYS.map((_, i) => ({ active: i >= 1 && i <= 5, startTime: "09:00", endTime: "18:00" }));
}

function Page() {
  const tenantId = useWorkspaceTenantId();
  const { professionalId: professionalIdFromUrl } = Route.useSearch();
  const [loading, setLoading] = useState(true);
  const [professionals, setProfessionals] = useState<ServiceProfessional[]>([]);
  const [professionalId, setProfessionalId] = useState("");
  const [week, setWeek] = useState<DayRow[]>(emptyWeek());
  const [saving, setSaving] = useState(false);
  const [copySourceId, setCopySourceId] = useState("");
  const [copying, setCopying] = useState(false);
  const [services, setServices] = useState<ServiceCatalogItem[]>([]);
  const [previewServiceId, setPreviewServiceId] = useState("");
  const [previewDate, setPreviewDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [previewSlots, setPreviewSlots] = useState<string[] | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const loadProfessionals = useCallback(async () => {
    if (!tenantId) return;
    const [p, s] = await Promise.all([
      apiGetJson<{ items: ServiceProfessional[] }>("/api/service-admin?resource=professionals", tenantId),
      apiGetJson<{ items: ServiceCatalogItem[] }>("/api/service-admin?resource=services", tenantId),
    ]);
    setProfessionals(p.items ?? []);
    setServices(s.items ?? []);
    setPreviewServiceId((prev) => prev || s.items?.[0]?.id || "");
    const pid = professionalIdFromUrl || professionalId || p.items?.[0]?.id || "";
    if (pid && pid !== professionalId) setProfessionalId(pid);
  }, [tenantId, professionalIdFromUrl, professionalId]);

  useEffect(() => {
    void loadProfessionals();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- solo al montar / cambiar el deep-link
  }, [tenantId, professionalIdFromUrl]);

  const loadSchedule = useCallback(async () => {
    if (!tenantId || !professionalId) return;
    setLoading(true);
    try {
      const s = await apiGetJson<{ items: ServiceSchedule[] }>(
        `/api/service-admin?resource=schedules&professionalId=${professionalId}`,
        tenantId,
      );
      const next = emptyWeek();
      for (const row of s.items ?? []) {
        if (row.dayOfWeek >= 0 && row.dayOfWeek <= 6) {
          next[row.dayOfWeek] = { active: true, startTime: row.startTime, endTime: row.endTime };
        }
      }
      // Días sin franja cargada quedan inactivos (no el default de lunes a viernes).
      const loadedDays = new Set((s.items ?? []).map((r) => r.dayOfWeek));
      for (let i = 0; i < 7; i++) {
        if (!loadedDays.has(i)) next[i] = { ...next[i], active: false };
      }
      setWeek(next);
    } finally {
      setLoading(false);
    }
  }, [tenantId, professionalId]);

  useEffect(() => {
    void loadSchedule();
  }, [loadSchedule]);

  async function save() {
    if (!tenantId || !professionalId) return;
    setSaving(true);
    try {
      const ranges = week
        .map((row, dayOfWeek) => ({ dayOfWeek, ...row }))
        .filter((row) => row.active)
        .map(({ dayOfWeek, startTime, endTime }) => ({ dayOfWeek, startTime, endTime }));
      await apiSendJson("/api/service-admin?resource=schedules", "POST", { professionalId, ranges }, tenantId);
      toast.success("Horario guardado");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Error al guardar");
    } finally {
      setSaving(false);
    }
  }

  // SCRUM-420: "copiar horarios" — trae el horario de otro profesional y lo
  // vuelca en el formulario actual (sin persistir todavía: el usuario sigue
  // usando el botón "Guardar horario" para confirmar, mismo flujo que editar
  // manualmente cada franja).
  async function copyScheduleFrom(sourceId: string) {
    if (!tenantId || !sourceId) return;
    setCopying(true);
    try {
      const s = await apiGetJson<{ items: ServiceSchedule[] }>(
        `/api/service-admin?resource=schedules&professionalId=${sourceId}`,
        tenantId,
      );
      const next = emptyWeek();
      const loadedDays = new Set<number>();
      for (const row of s.items ?? []) {
        if (row.dayOfWeek >= 0 && row.dayOfWeek <= 6) {
          next[row.dayOfWeek] = { active: true, startTime: row.startTime, endTime: row.endTime };
          loadedDays.add(row.dayOfWeek);
        }
      }
      for (let i = 0; i < 7; i++) {
        if (!loadedDays.has(i)) next[i] = { ...next[i], active: false };
      }
      setWeek(next);
      const sourceName = professionals.find((p) => p.id === sourceId)?.name ?? "el profesional elegido";
      toast.success(`Horario de ${sourceName} copiado — revisá y confirmá con "Guardar horario"`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "No se pudo copiar el horario");
    } finally {
      setCopying(false);
    }
  }

  // SCRUM-420: "resumen de disponibilidad efectiva" — reusa el mismo motor de
  // slots que ya usa la creación de turnos (dbSmGetAvailableServiceSlots),
  // en vez de reimplementar el cruce horario+bloqueos+turnos ya tomados.
  async function loadAvailabilityPreview() {
    if (!tenantId || !professionalId || !previewServiceId || !previewDate) return;
    setPreviewLoading(true);
    try {
      const r = await apiGetJson<{ slots: string[]; valid: boolean }>(
        `/api/service-bookings?resource=slots&professionalId=${professionalId}&serviceId=${previewServiceId}&date=${previewDate}`,
        tenantId,
      );
      if (!r.valid) {
        toast.error("Ese servicio no está vinculado a este profesional");
        setPreviewSlots([]);
        return;
      }
      setPreviewSlots(r.slots ?? []);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "No se pudo calcular la disponibilidad");
      setPreviewSlots(null);
    } finally {
      setPreviewLoading(false);
    }
  }

  return (
    <ClinicalPageShell offset="none" className="pt-6">
      <MerchantExperienceBar screenId="horarios" />
      <CrudShellHeader title="Horarios" />
      <div className="px-4 pb-8">
        <Tabs defaultValue="horarios" className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="horarios">Por profesional</TabsTrigger>
            <TabsTrigger value="bloqueos">Bloqueos</TabsTrigger>
          </TabsList>

          <TabsContent value="horarios" className="space-y-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
              <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                Profesional
                <Select value={professionalId} onValueChange={setProfessionalId}>
                  <SelectTrigger className="w-56"><SelectValue placeholder="Elegir…" /></SelectTrigger>
                  <SelectContent>
                    {professionals.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </label>
              <Button size="sm" disabled={saving || !professionalId} onClick={() => void save()}>
                {saving ? "Guardando…" : "Guardar horario"}
              </Button>
              <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                Copiar horario de…
                <Select value={copySourceId} onValueChange={setCopySourceId}>
                  <SelectTrigger className="w-56"><SelectValue placeholder="Elegir profesional…" /></SelectTrigger>
                  <SelectContent>
                    {professionals.filter((p) => p.id !== professionalId).map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </label>
              <Button
                size="sm"
                variant="outline"
                className="gap-2"
                disabled={copying || !copySourceId}
                onClick={() => void copyScheduleFrom(copySourceId)}
              >
                <Copy className="h-3.5 w-3.5" />
                {copying ? "Copiando…" : "Copiar"}
              </Button>
            </div>
            {loading ? (
              <Loading />
            ) : !professionalId ? (
              <p className="text-sm text-muted-foreground">No hay profesionales cargados todavía.</p>
            ) : (
              <div className="space-y-2">
                {week.map((row, i) => (
                  <div key={i} className="flex flex-wrap items-center gap-3 rounded-lg border bg-card p-3">
                    <label className="flex w-32 shrink-0 items-center gap-2 text-sm font-medium">
                      <Switch
                        checked={row.active}
                        onCheckedChange={(v) => setWeek((w) => w.map((r, idx) => (idx === i ? { ...r, active: v } : r)))}
                      />
                      {DAYS[i]}
                    </label>
                    {row.active ? (
                      <div className="flex items-center gap-2">
                        <Input
                          type="time"
                          className="w-32"
                          value={row.startTime}
                          onChange={(e) => setWeek((w) => w.map((r, idx) => (idx === i ? { ...r, startTime: e.target.value } : r)))}
                        />
                        <span className="text-xs text-muted-foreground">a</span>
                        <Input
                          type="time"
                          className="w-32"
                          value={row.endTime}
                          onChange={(e) => setWeek((w) => w.map((r, idx) => (idx === i ? { ...r, endTime: e.target.value } : r)))}
                        />
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">Sin atención</span>
                    )}
                  </div>
                ))}
              </div>
            )}

            {professionalId ? (
              <section className="space-y-3 rounded-xl border bg-card p-4">
                <h2 className="flex items-center gap-2 text-sm font-semibold">
                  <CalendarSearch className="h-4 w-4" /> Disponibilidad efectiva
                </h2>
                <p className="text-xs text-muted-foreground">
                  Cruza el horario cargado con bloqueos y turnos ya tomados — los mismos slots que vería un cliente al reservar.
                </p>
                <div className="flex flex-wrap items-end gap-3">
                  <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                    Servicio de referencia
                    <Select value={previewServiceId} onValueChange={setPreviewServiceId}>
                      <SelectTrigger className="w-56"><SelectValue placeholder="Elegir servicio…" /></SelectTrigger>
                      <SelectContent>
                        {services.map((s) => (
                          <SelectItem key={s.id} value={s.id}>{s.name} ({s.durationMinutes} min)</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </label>
                  <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                    Fecha
                    <Input type="date" className="w-40" value={previewDate} onChange={(e) => setPreviewDate(e.target.value)} />
                  </label>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={previewLoading || !previewServiceId || !previewDate}
                    onClick={() => void loadAvailabilityPreview()}
                  >
                    {previewLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Ver disponibilidad"}
                  </Button>
                </div>
                {previewSlots != null ? (
                  previewSlots.length === 0 ? (
                    <p className="text-xs text-muted-foreground">Sin horarios libres ese día para ese servicio.</p>
                  ) : (
                    <div className="flex flex-wrap gap-1.5" data-testid="availability-preview-slots">
                      {previewSlots.map((slot) => (
                        <span key={slot} className="rounded-md border bg-muted/40 px-2 py-1 text-xs">{slot}</span>
                      ))}
                    </div>
                  )
                ) : null}
              </section>
            ) : null}
          </TabsContent>

          <TabsContent value="bloqueos">
            <AgendaBlocksTab tenantId={tenantId} professionals={professionals} />
          </TabsContent>
        </Tabs>
      </div>
    </ClinicalPageShell>
  );
}

function AgendaBlocksTab({
  tenantId,
  professionals,
}: {
  tenantId: string | null | undefined;
  professionals: ServiceProfessional[];
}) {
  const [blocks, setBlocks] = useState<AgendaBlock[]>([]);
  const [loading, setLoading] = useState(true);
  const [profId, setProfId] = useState("");
  const [date, setDate] = useState("");
  const [allDay, setAllDay] = useState(true);
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("13:00");
  const [label, setLabel] = useState("");
  const [saving, setSaving] = useState(false);

  const today = new Date().toISOString().slice(0, 10);

  const loadBlocks = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const r = await apiGetJson<{ items: AgendaBlock[] }>("/api/service-admin?resource=blocks", tenantId);
      setBlocks(r.items ?? []);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "No se pudo cargar");
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    void loadBlocks();
  }, [loadBlocks]);

  async function create() {
    if (!tenantId) return;
    if (!label.trim()) {
      toast.error("Completá la etiqueta");
      return;
    }
    if (!date) {
      toast.error("Elegí una fecha");
      return;
    }
    setSaving(true);
    try {
      await apiSendJson(
        "/api/service-admin?resource=blocks",
        "POST",
        {
          professionalId: profId || null,
          blockDate: date,
          startTime: allDay ? null : startTime,
          endTime: allDay ? null : endTime,
          label: label.trim(),
        },
        tenantId,
      );
      toast.success("Bloqueo creado");
      setLabel("");
      setDate("");
      await loadBlocks();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Error al crear bloqueo");
    } finally {
      setSaving(false);
    }
  }

  async function remove(id: string) {
    if (!tenantId) return;
    try {
      await apiDelete(`/api/service-admin?resource=blocks&id=${id}`, tenantId);
      toast.success("Eliminado");
      await loadBlocks();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "No se pudo eliminar");
    }
  }

  return (
    <div className="space-y-6">
      <section className="space-y-4 rounded-xl border bg-card p-5">
        <h2 className="text-sm font-semibold">Nuevo bloqueo</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label className="text-xs">Profesional</Label>
            <Select value={profId || "__all"} onValueChange={(v) => setProfId(v === "__all" ? "" : v)}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="Ámbito" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">Todo el negocio</SelectItem>
                {professionals.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Fecha</Label>
            <Input type="date" className="mt-1" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Switch checked={!allDay} onCheckedChange={(v) => setAllDay(!v)} />
          <Label className="!mb-0 text-sm">Franja horaria específica (si no, bloquea el día completo)</Label>
        </div>
        {!allDay ? (
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label className="text-xs">Desde hora</Label>
              <Input type="time" className="mt-1" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Hasta hora</Label>
              <Input type="time" className="mt-1" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
            </div>
          </div>
        ) : null}
        <div>
          <Label className="text-xs">Etiqueta</Label>
          <Input className="mt-1" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Vacaciones, feriado…" />
        </div>
        <Button type="button" disabled={saving} onClick={() => void create()}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Agregar bloqueo"}
        </Button>
      </section>

      <section className="space-y-3">
        <h2 className="text-sm font-semibold">Bloqueos activos</h2>
        {loading ? (
          <Loading />
        ) : blocks.length === 0 ? (
          <p className="text-sm text-muted-foreground">No hay bloqueos cargados.</p>
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            {blocks.map((b) => (
              <div key={b.id} className="flex flex-col gap-2 rounded-xl border bg-card p-4">
                <p className="text-sm font-medium">{b.label ?? "Sin etiqueta"}</p>
                <p className="text-xs text-muted-foreground">
                  {b.blockDate}
                  {b.startTime && b.endTime ? ` · ${b.startTime}–${b.endTime}` : " · Día completo"}
                  {b.blockDate < today ? " · Pasado" : b.blockDate === today ? " · Hoy" : ""}
                </p>
                <p className="text-xs text-muted-foreground">
                  {b.professionalId ? professionals.find((p) => p.id === b.professionalId)?.name ?? "Profesional" : "Todo el negocio"}
                </p>
                <Button type="button" variant="ghost" size="sm" className="self-end text-destructive" onClick={() => void remove(b.id)}>
                  <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                  Eliminar
                </Button>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
